import { getAuthedDataClient, getPublicDataClient } from '@/lib/amplify-client';
import {
  applyPlatformFeeSettings,
  DEFAULT_PLATFORM_SETTINGS,
  type PlatformSettingsFees,
} from '@/lib/platformFees';
import {
  DEFAULT_SUBSCRIPTION_LAUNCH,
  type SubscriptionLaunchSettings,
} from '@/lib/subscriptionLaunch';
import { setDeliveryHighlightOverride } from '@/lib/platformCopy';
import {
  applyPlanDefinitions,
  parsePlanDefinitionsJson,
  resolvePlanDefinitionsFromSettings,
} from '@/lib/platformPlanConfig';
import { applyListingSurfaces, parseListingSurfacesJson } from '@/lib/platformSurfaceConfig';
import { applyFeatureFlags, parseFeatureFlagsJson } from '@/lib/platformFeatureFlagsRuntime';
import { applyHomepageHero, parseHomepageHeroJson } from '@/lib/platformHeroConfig';
import { applySiteSocialLinks, parseSiteSocialLinksJson } from '@/lib/platformSiteSocial';

export type PlatformSettings = PlatformSettingsFees &
  SubscriptionLaunchSettings & {
  id: string;
  deliveryCopy?: string;
  planDefinitionsJson?: string;
  listingSurfacesJson?: string;
  featureFlagsJson?: string;
  homepageHeroJson?: string;
  siteSocialLinksJson?: string;
  updatedAt?: string;
};

const SETTINGS_ID = 'default';

let defaultSettings: PlatformSettings = {
  id: SETTINGS_ID,
  ...DEFAULT_PLATFORM_SETTINGS,
  ...DEFAULT_SUBSCRIPTION_LAUNCH,
  deliveryCopy: undefined,
  updatedAt: new Date().toISOString(),
};

function mapRow(row: Record<string, unknown>): PlatformSettings {
  return {
    id: String(row.id),
    convenienceFeeBps: Number(row.convenienceFeeBps ?? DEFAULT_PLATFORM_SETTINGS.convenienceFeeBps),
    defaultCommissionBps: Number(row.defaultCommissionBps ?? DEFAULT_PLATFORM_SETTINGS.defaultCommissionBps),
    starterCommissionBps: Number(row.starterCommissionBps ?? DEFAULT_PLATFORM_SETTINGS.starterCommissionBps),
    growthCommissionBps: Number(row.growthCommissionBps ?? DEFAULT_PLATFORM_SETTINGS.growthCommissionBps),
    proCommissionBps: Number(row.proCommissionBps ?? DEFAULT_PLATFORM_SETTINGS.proCommissionBps),
    deliveryCopy: row.deliveryCopy ? String(row.deliveryCopy) : undefined,
    subscriptionsFreeLaunchEnabled:
      row.subscriptionsFreeLaunchEnabled === undefined
        ? DEFAULT_SUBSCRIPTION_LAUNCH.subscriptionsFreeLaunchEnabled
        : row.subscriptionsFreeLaunchEnabled === true,
    subscriptionsFreeUntil: row.subscriptionsFreeUntil
      ? String(row.subscriptionsFreeUntil)
      : DEFAULT_SUBSCRIPTION_LAUNCH.subscriptionsFreeUntil,
    subscriptionsFreeMessage: row.subscriptionsFreeMessage
      ? String(row.subscriptionsFreeMessage)
      : undefined,
    planDefinitionsJson: row.planDefinitionsJson ? String(row.planDefinitionsJson) : undefined,
    listingSurfacesJson: row.listingSurfacesJson ? String(row.listingSurfacesJson) : undefined,
    featureFlagsJson: row.featureFlagsJson ? String(row.featureFlagsJson) : undefined,
    homepageHeroJson: row.homepageHeroJson ? String(row.homepageHeroJson) : undefined,
    siteSocialLinksJson: row.siteSocialLinksJson ? String(row.siteSocialLinksJson) : undefined,
    updatedAt: row.updatedAt ? String(row.updatedAt) : undefined,
  };
}

function syncRuntime(settings: PlatformSettings): void {
  applyPlatformFeeSettings(settings);
  setDeliveryHighlightOverride(settings.deliveryCopy ?? null);
  applyPlanDefinitions(resolvePlanDefinitionsFromSettings(settings));
  applyListingSurfaces(parseListingSurfacesJson(settings.listingSurfacesJson));
  applyFeatureFlags(parseFeatureFlagsJson(settings.featureFlagsJson));
  applyHomepageHero(parseHomepageHeroJson(settings.homepageHeroJson));
  applySiteSocialLinks(parseSiteSocialLinksJson(settings.siteSocialLinksJson));
}

type AppSyncResult = { data?: unknown; errors?: { message?: string }[] };

function appSyncErrorMessage(res: AppSyncResult, fallback: string): string {
  const msg = res.errors?.map((e) => e.message).filter(Boolean).join('; ');
  return msg || fallback;
}

function throwIfAppSyncFailed(res: AppSyncResult, fallback: string): void {
  if (res.data) return;
  throw new Error(appSyncErrorMessage(res, fallback));
}

type PlatformSettingsWrite = {
  id: string;
  convenienceFeeBps: number;
  defaultCommissionBps: number;
  starterCommissionBps: number;
  growthCommissionBps: number;
  proCommissionBps: number;
  deliveryCopy?: string | null;
  subscriptionsFreeLaunchEnabled: boolean;
  subscriptionsFreeUntil?: string | null;
  subscriptionsFreeMessage?: string | null;
  planDefinitionsJson?: string | null;
  listingSurfacesJson?: string | null;
  featureFlagsJson?: string | null;
  homepageHeroJson?: string | null;
  siteSocialLinksJson?: string | null;
};

function buildWritePayload(
  patch: Partial<Omit<PlatformSettings, 'id' | 'updatedAt'>>,
  existing?: PlatformSettings,
): PlatformSettingsWrite {
  const base = existing ?? {
    id: SETTINGS_ID,
    ...DEFAULT_PLATFORM_SETTINGS,
    ...DEFAULT_SUBSCRIPTION_LAUNCH,
  };
  const merged = { ...base, ...patch, id: SETTINGS_ID };
  const payload: PlatformSettingsWrite = {
    id: SETTINGS_ID,
    convenienceFeeBps: merged.convenienceFeeBps,
    defaultCommissionBps: merged.defaultCommissionBps,
    starterCommissionBps: merged.starterCommissionBps,
    growthCommissionBps: merged.growthCommissionBps,
    proCommissionBps: merged.proCommissionBps,
    subscriptionsFreeLaunchEnabled: merged.subscriptionsFreeLaunchEnabled,
  };
  if (merged.deliveryCopy !== undefined) {
    payload.deliveryCopy = merged.deliveryCopy?.trim() ? merged.deliveryCopy.trim() : null;
  }
  if (merged.subscriptionsFreeUntil !== undefined) {
    payload.subscriptionsFreeUntil = merged.subscriptionsFreeUntil ?? null;
  }
  if (merged.subscriptionsFreeMessage !== undefined) {
    payload.subscriptionsFreeMessage = merged.subscriptionsFreeMessage?.trim()
      ? merged.subscriptionsFreeMessage.trim()
      : null;
  }
  if (merged.planDefinitionsJson !== undefined) {
    payload.planDefinitionsJson = merged.planDefinitionsJson?.trim()
      ? merged.planDefinitionsJson.trim()
      : null;
  }
  if (merged.listingSurfacesJson !== undefined) {
    payload.listingSurfacesJson = merged.listingSurfacesJson?.trim()
      ? merged.listingSurfacesJson.trim()
      : null;
  }
  if (merged.featureFlagsJson !== undefined) {
    payload.featureFlagsJson = merged.featureFlagsJson?.trim()
      ? merged.featureFlagsJson.trim()
      : null;
  }
  if (merged.homepageHeroJson !== undefined) {
    payload.homepageHeroJson = merged.homepageHeroJson?.trim()
      ? merged.homepageHeroJson.trim()
      : null;
  }
  if (merged.siteSocialLinksJson !== undefined) {
    payload.siteSocialLinksJson = merged.siteSocialLinksJson?.trim()
      ? merged.siteSocialLinksJson.trim()
      : null;
  }
  return payload;
}

async function fetchLivePlatformSettingsRow(): Promise<Record<string, unknown> | null> {
  const client = await getAuthedDataClient();
  const res = await client.models.PlatformSettings.get({ id: SETTINGS_ID });
  return (res.data as Record<string, unknown> | null | undefined) ?? null;
}

export async function getPlatformSettings(): Promise<PlatformSettings> {
  try {
    const client = await getPublicDataClient();
    const res = await client.models.PlatformSettings.get({ id: SETTINGS_ID });
    if (res.data) {
      const mapped = mapRow(res.data as Record<string, unknown>);
      syncRuntime(mapped);
      return mapped;
    }
    if (res.errors?.length) {
      console.warn('[ShopGND] PlatformSettings.get', res.errors);
    }
  } catch (e) {
    console.warn('[ShopGND] Could not load platform settings', e);
  }
  syncRuntime(defaultSettings);
  return defaultSettings;
}

export async function updatePlatformSettings(
  patch: Partial<Omit<PlatformSettings, 'id' | 'updatedAt'>>,
): Promise<PlatformSettings> {
  const client = await getAuthedDataClient();
  const existingRow = await fetchLivePlatformSettingsRow();
  const existing = existingRow ? mapRow(existingRow) : undefined;
  const payload = buildWritePayload(patch, existing);

  const res = existingRow
    ? await client.models.PlatformSettings.update(payload)
    : await client.models.PlatformSettings.create(payload);

  if (!res.data) {
    const fallback = existingRow
      ? 'Could not update platform settings'
      : 'Could not create platform settings';
    throwIfAppSyncFailed(res, fallback);
  }

  const mapped = mapRow(res.data as Record<string, unknown>);
  syncRuntime(mapped);
  return mapped;
}
